import { AppElapsedDays } from "./AppElapsedDays";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppTaskRules } from "./AppTaskRules";
import { AppContact, AppIndex, AppMoney, AppRow } from "./AppIndex";
import { AppSearch } from "./AppSearch";
import { AppAvatarRules } from "./AppAvatars";
import { AppChatStanding } from "./AppChatStanding";
import { AppChatQuickView } from "./AppChatQuickView";
import { AppIcons } from "./AppIcons";
import { AppChatMarksRules } from "./AppChatMarksRules";

// The three views the owner asked for: Chats, Tasks, Revenue.
//
// ⚠ THEY ARE VIEWS, NOT SECTIONS (decision 7, ruled 2026-09-12). The harvest never files a fact
// under "tasks" or "revenue"; it discovers its own groups per contact, and these screens select
// across all of them. That is why a plumber, a school and a landlord can each have a page shaped
// like themselves and still appear in one Tasks list.
//
// ⚠ AND THEY ARE BROWSED, NOT READ (owner ruling, 2026-09-12). Same budget as the contact page:
// six words a value, nothing repeated that is already beside it, no sentence where a token will do.

export class AppViews {
    /**
     * ⚠ EVERY EMPTY VIEW MUST SAY WHICH KIND OF EMPTY IT IS. "No open tasks" and "nobody has read
     * these conversations yet" look identical on screen and mean opposite things - one is a clean
     * slate, the other is 81 unread conversations. The index carries `harvested` precisely so this
     * can be answered honestly.
     */
    public static Empty = (What: string, Index: AppIndex): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Unread: number = Index.Unread();
            if (Unread <= 0) { return "<p class=\"none\">No " + What + ".</p>"; }
            // ⛔ SINGULAR AND PLURAL AGREE ("1 conversation ... has", never "1 have").
            let Read: string = String(Index.Counts.Harvested) + " conversations read so far";
            if (Index.Counts.Harvested === 1) { Read = "1 conversation read so far"; }
            let Never: string = String(Unread) + " have never been analysed";
            if (Unread === 1) { Never = "1 has never been analysed"; }
            return "<p class=\"none\">No " + What + " <b>in the " + Read + "</b>. " + Never
                + ", so this is not yet a complete picture.</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Empty");
            return "";
        }
    };

    /** How many days after its last message an unanalysed 1:1 chat counts as a fault (`StaleUnanalysed`). */
    public static readonly StaleAfterDays: number = 3;

    /**
     * The 1:1 chats with a page that are still not analysed (`Harvested` false) more than `StaleAfterDays` after their last
     * message - "" days (no last message) never count. DOCUMENTED EXCEPTION: pure count over the index.
     */
    public static StaleUnanalysed = (Index: AppIndex): number => {
        let N: number = 0;
        for (const One of Index.Contacts) {
            if (One.IsGroup === true || One.Harvested === true || One.Href.length === 0) { continue; }
            if (One.DaysSinceLast > AppViews.StaleAfterDays) { N = N + 1; }
        }
        return N;
    };

    /**
     * The coverage line ("Analysed 75 of 76 conversations. The other one is not in these lists yet."), or "" at full
     * coverage. ⛔ SINGULAR AND PLURAL AGREE (found live 2026-09-30: "The other 1 are not in these lists yet").
     */
    public static CoverageLine = (Index: AppIndex): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Unread: number = Index.Unread();
            if (Unread <= 0) { return ""; }
            let Rest: string = "The other " + Unread.toLocaleString() + " are not in these lists yet.";
            if (Unread === 1) { Rest = "The other one is not in these lists yet."; }
            return "<p class=\"cover\">Analysed <b>" + Index.Counts.Harvested.toLocaleString() + " of "
                + Index.Contacts.length.toLocaleString() + "</b> conversations. " + Rest + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.CoverageLine");
            return "";
        }
    };

    /**
     * ⭐ EVERY VIEW HAS ONE `<h1>` (audit 2026-10-01): the list views (Chats, Tasks, Overview, Revenue, Sent) had no
     * heading above their sections, so the page's outline started at an `<h2>` or `<h3>` and "go to the main heading"
     * found nothing. The view's name is already on screen as the lit tab, so the heading is for a screen reader
     * (`.sr`) - no row of the screen is spent on it.
     */
    public static ViewHead = (Word: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Word).length === 0) { return ""; }
            return "<h1 class=\"sr\">" + kHTMLCore.EscapeHtml(Word) + "</h1>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.ViewHead");
            return "";
        }
    };

    /** A list route's name, as its tab says it. */
    public static ViewName = (Route: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Route === "tasks") { return "Tasks"; }
            if (Route === "money") { return "Revenue"; }
            if (Route === "overview") { return "Overview"; }
            if (Route === "sent") { return "Sent"; }
            return "Chats";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.ViewName");
            return "";
        }
    };

    // ── Chats ────────────────────────────────────────────────────────────────────────────────

    /** The ids the live search is wired by. ⚠ `AppMain` binds and writes these; they are the seam. */
    public static readonly FindId: string = "wac-find";
    public static readonly ListId: string = "wac-chatlist";
    public static readonly FoundId: string = "wac-found";
    /** ⭐ The route of a chat with no page yet (`#n/<chat id>`, `AppMain.NewChat`) - its provisional page. */
    public static readonly NewRoute: string = "n";

    /**
     * The Chats view - the main screen: the search box FIRST, then `Between` (the tabs and the
     * coverage line), then the cards, then the message hits.
     *
     * ⭐ THE BOX IS THE PRODUCT'S SEARCH (2026-09-25), so it sits above the tabs: names, numbers AND
     * the words in the messages (`AppSearch`).
     * ⚠ THE BOX, THE LIST AND THE HITS ARE SEPARATE ELEMENTS ON PURPOSE. Typing re-renders only
     * `wac-chatlist` and `wac-msghits`; re-rendering the input as well would throw away focus and the
     * caret on every keystroke, and on a phone it would close the keyboard after each letter.
     */
    public static Chats = (Index: AppIndex, Filter: string, Between: string, InBar: boolean = false,
        Current: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Index.Contacts.length === 0) { return Between + AppViews.Empty("chats", Index); }
            // ⭐ THE TOP BAR (design item 7, 2026-09-30) holds the box when it is mounted (`InBar`): same markup, same ids,
            // placed there by `AppTopBarRun.ShowSearch` - so it is not drawn here a second time.
            let Box: string = AppViews.SearchBox(Index, Filter);
            if (InBar === true) { Box = ""; }
            return Box + Between + "<div id=\"" + AppViews.ListId + "\">"
                + AppViews.Results(Index, Filter, Current) + "</div><section class=\"msghits\" id=\""
                + AppSearch.HitsId + "\" aria-live=\"polite\" hidden></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Chats");
            return "";
        }
    };

    /**
     * ⚠ A REAL `<label>`, NOT A PLACEHOLDER POSING AS ONE. A placeholder disappears the moment
     * somebody types, and a screen reader is not obliged to read it at all. The count line is
     * `aria-live` so a filtered list is ANNOUNCED - a list that silently shrinks is invisible to
     * anybody who cannot see it shrink.
     */
    public static SearchBox = (Index: AppIndex, Filter: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⚠ THE LABEL IS STILL A REAL `<label>` (read by a screen reader, and whatever is typed keeps its
            // name), only VISUALLY hidden: a line of "SEARCH" over a box that says what it searches took a row of
            // the screen for nothing (owner, 2026-09-28). The tooltip carries it for the eye (no placeholder, owner 2026-11-02).
            // ⭐ A NAMED search landmark: the Tasks tab has one too ("Search tasks"), and two unnamed ones cannot be told apart.
            return "<div class=\"find\" role=\"search\" aria-label=\"Search chats\"><label class=\"sr\" for=\"" + AppViews.FindId + "\">"
                // ⭐ The magnifier inside the field (business look wave B): decoration - the label above names the field.
                + "Search</label><span class=\"findico\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Search, 16) + "</span><input id=\"" + AppViews.FindId + "\" type=\"search\""
                + " autocomplete=\"off\" spellcheck=\"false\" placeholder=\" \""
                + " title=\"Search names, numbers and the words of your messages\""
                + " value=\"" + kHTMLCore.EscapeAttr(Filter) + "\">"
                + "<p class=\"found\" id=\"" + AppViews.FoundId + "\" aria-live=\"polite\">"
                + kHTMLCore.EscapeHtml(AppViews.FoundText(Index, Filter)) + "</p></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.SearchBox");
            return "";
        }
    };

    /** The cards matching `Filter`, or a sentence saying nothing matched. */
    public static Results = (Index: AppIndex, Filter: string, Current: string = "", Include: ((One: AppContact) => boolean) | null = null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Rows: AppContact[] = AppViews.Filtered(Index.Contacts, Filter);
            if (Include !== null) { Rows = Rows.filter(Include); }
            if (Rows.length === 0 && Include !== null && Filter.trim().length === 0) { return "<p class=\"none\">No chats in this view.</p>"; }
            if (Rows.length === 0 && Index.Contacts.length > 0) {
                // ⚠ NOT `Empty`. "No chats in the 3 conversations read so far" would say the
                // archive is thin when the truth is that the SEARCH matched nothing.
                // ⚠ "NAMES OR NUMBERS": the messages are searched too, and answer underneath.
                // ⭐ OWNER 2026-11-01: short - "No matches", no explanation.
                return "<p class=\"none\">No matches</p>";
            }
            if (Rows.length === 0) { return AppViews.Empty("chats", Index); }
            // ⭐ ROWS (the inbox, the default) OR CARDS (design item 8, 2026-09-30): the SAME `li.card` markup either way - only
            // the list's class changes, so every hook (typing line, live counts, avatars, shortcuts, harnesses) holds for both.
            // ⭐ ROWS, the one look (the Rows / Cards switch was removed 2026-10-14). ⚠ `cards asrows`: every hook (typing
            // line, live counts, avatars, shortcuts, harnesses) reads `li.card`, and `asrows` is what lays it out as a row.
            return AppViews.Sections(AppViews.NewestFirst(Rows), Current, Include === null && Filter.trim().length === 0);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Results");
            return "";
        }
    };

    /**
     * ⭐ PINNED, THEN LATEST (owner 2026-11-01: "Redesign the first page - focus on latest and pinned chats"): on the
     * unfiltered list the chats pinned on the phone get their own "Pinned" list, then "Latest" holds the rest, newest first.
     * The order is `NewestFirst`'s (pinned first) either way, so Alt+Up/Down and the live moves are unchanged. ⚠ Every list is
     * `ul.cards.asrows` of `li.card` - the hooks read those. No pinned chat, or a search = one list, no headings.
     */
    public static Sections = (Sorted: AppContact[], Current: string, Split: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Pinned: AppContact[] = [];
            const Rest: AppContact[] = [];
            for (const One of Sorted) { if (Split === true && One.PinnedAt > 0) { Pinned.push(One); } else { Rest.push(One); } }
            if (Pinned.length === 0) { return AppViews.ListHead + AppViews.CardList(Rest, Current); }
            let Out: string = "<h2 class=\"listsec\">Pinned</h2>" + AppViews.CardList(Pinned, Current);
            if (Rest.length > 0) { Out = Out + "<h2 class=\"listsec\">Latest</h2>" + AppViews.ListHead + AppViews.CardList(Rest, Current); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Sections");
            return "";
        }
    };

    // DOCUMENTED EXCEPTION: pure markup (the caller guards).
    private static CardList = (Rows: AppContact[], Current: string): string => {
        const Out: string[] = ["<ul class=\"cards asrows\">"];
        for (const One of Rows) { Out.push(AppViews.Card(One, Current)); }
        Out.push("</ul>");
        return Out.join("");
    };

    /**
     * ⭐ THE CHAT LIST IS NEWEST FIRST, LIKE WHATSAPP (owner P0, 2026-09-28: new chats "not yet displayed").
     * `index.json` is ranked by size, so a conversation that began this morning with 16 messages sat at
     * the BOTTOM of ~100 cards - on the list and invisible. A COPY is sorted (the index keeps its order
     * for everything else); an equal or missing `lastIso` keeps the index's order (a stable sort).
     */
    public static NewestFirst = (Rows: AppContact[]): AppContact[] => {
        if (getCoreUtils().KErrorMode === true) { return Rows; }
        try {
            const Out: AppContact[] = Rows.slice();
            Out.sort(AppViews.ByLastDesc);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.NewestFirst");
            return Rows;
        }
    };

    /** ⭐ Chats pinned on the phone first, newest pin first (`AppChatMarks`); then ISO UTC strings compare as text; "" sorts last. */
    private static ByLastDesc = (A: AppContact, B: AppContact): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (A.PinnedAt !== B.PinnedAt) { if (A.PinnedAt > B.PinnedAt) { return -1; } return 1; }
            if (A.LastIso === B.LastIso) { return 0; }
            if (A.LastIso > B.LastIso) { return -1; }
            return 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.ByLastDesc");
            return 0;
        }
    };

    /** "12 of 52 chats", or "" with no filter - the tab already carries the total. */
    public static FoundText = (Index: AppIndex, Filter: string, Include: ((One: AppContact) => boolean) | null = null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Filter.trim().length === 0) { return ""; }
            let Contacts: AppContact[] = Index.Contacts;
            if (Include !== null) { Contacts = Contacts.filter(Include); }
            const Hits: number = AppViews.Filtered(Contacts, Filter).length;
            return Hits.toLocaleString() + " of " + Contacts.length.toLocaleString() + " chats";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.FoundText");
            return "";
        }
    };

    /**
     * ⚠ CASE- AND ACCENT-FREE ON BOTH SIDES (`AppSearch.Fold`): "ostgota" finds "Östgöta", and a
     * phone keyboard that will not type "ö" can still find Björn.
     */
    public static Filtered = (Rows: AppContact[], Filter: string): AppContact[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Want: string = AppSearch.Fold(Filter).trim();
            if (Want.length === 0) { return Rows; }
            const Digits: string = AppViews.PhoneDigits(Want);
            const Out: AppContact[] = [];
            for (const One of Rows) {
                const Hay: string = AppSearch.Fold(One.Title + " " + One.Relationship + " " + One.Summary
                    + " " + One.PersonKey);
                if (Hay.indexOf(Want) >= 0) { Out.push(One); continue; }
                if (Digits.length === 0) { continue; }
                const Theirs: string = (One.PersonKey + " " + One.ChatId).replace(/\D/g, "");
                if (Theirs.indexOf(Digits) >= 0) { Out.push(One); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Filtered");
            return [];
        }
    };

    /**
     * The digits of a search that LOOKS LIKE A NUMBER, or "" for one that does not.
     *
     * ⚠ PEOPLE TYPE NUMBERS THE WAY THEY SAY THEM - "070 123 45", "+46 70-123" - and the key is
     * stored bare (`+46701234567`), so a substring test on the raw text never matches. ⚠ The
     * leading 0 of a national number is dropped, because the key carries the country code in its
     * place; "0701" must still find "+46701...". Two digits minimum, or "4" matches half the list.
     */
    public static PhoneDigits = (Want: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (/^[\d\s+().\-]+$/.test(Want) === false) { return ""; }
            let Digits: string = Want.replace(/\D/g, "");
            if (Want.trim().charAt(0) !== "+") { Digits = Digits.replace(/^0+/, ""); }
            if (Digits.length < 2) { return ""; }
            return Digits;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.PhoneDigits");
            return "";
        }
    };

    /** A card answers "who is this and does it need me", in that order. The phone number and the
     *  message count are on the contact page; repeating them here is the browsing tax again. */
    private static Card = (One: AppContact, Current: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            let Cls: string = "card s-" + kHTMLCore.EscapeAttr(One.Standing);
            // ⭐ A CHAT THAT BEGAN AFTER THE LAST REBUILD (`AppLiveList`): no page yet - it opens the provisional one.
            let Link: string = "#c/" + encodeURIComponent(One.Href);
            if (One.IsNew === true) { Cls = Cls + " isnew"; Link = "#" + AppViews.NewRoute + "/" + encodeURIComponent(One.ChatId); }
            Out.push("<li class=\"" + Cls + "\" data-chat=\"" + kHTMLCore.EscapeAttr(One.ChatId) + "\">");
            // ⭐ THE OPEN CHAT'S ROW ON THE WIDE THREE-PANE SCREEN (`AppLayout`): `aria-current="page"` - its highlight too.
            let Now: string = "";
            if (Current.length > 0 && One.ChatId === Current) { Now = " aria-current=\"page\""; }
            // ⭐ THE WHOLE OF WHAT A ROW CUTS SHORT (audit 2026-10-12): a long name and the relationship line end in an
            // ellipsis, and nothing gave the rest - app.css already called the row's tooltip the place for it. The link's
            // `title` is the name and that line in full: a resting mouse, a held finger and the keyboard's focus all
            // show it (`AppTips`).
            Out.push("<a href=\"" + Link + "\"" + Now + " title=\"" + kHTMLCore.EscapeAttr(AppViews.WholeRow(One)) + "\">");
            // ⭐ THE CONTACT'S WHATSAPP PICTURE (or their initials) - laid on lazily by `AppAvatarLoader`.
            Out.push(AppAvatarRules.Disc(One.Title, One.Avatar));
            Out.push("<span class=\"t\">" + kHTMLCore.EscapeAttr(One.Title) + AppViews.PhoneMarks(One) + "</span>");
            Out.push(AppViews.Sub(One));
            Out.push("<span class=\"m\">" + AppViews.Meta(One) + "</span>");
            Out.push("</a>" + AppChatQuickView.Button(One) + "</li>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Card");
            return "";
        }
    };

    /**
     * A row's meta line (inside `span.m`): standing · how long ago · "N new" · "N to do". ⭐ Its own function so the wide
     * screen can redraw ONE row's line in place when the chat is opened (`AppThreeCol.MarkCurrent`) - the link survives.
     */
    public static Meta = (One: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ EACH PIECE IN ITS OWN SPAN (business look wave B): the same words in the same order - "Waiting on you  ·
            // 9 days  ·  1 to do" - so a card reads as before, while a compact row shows only the age and the badges (the
            // row's stripe says the standing) and the table lays the pieces out as its Status / Last message / Open tasks
            // columns (`app.css`, block `wac-structure`). `.mb` is always there, so a table's columns stay aligned.
            return "<span class=\"ms\">" + kHTMLCore.EscapeAttr(AppViews.StandingWords(One.Standing)) + "</span>" + AppViews.Dot
                + "<span class=\"ma\">" + kHTMLCore.EscapeAttr(AppViews.Ago(One.DaysSinceLast)) + "</span><span class=\"mb\">"
                + AppViews.Badge(One) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Meta");
            return "";
        }
    };

    /**
     * A row's name and second line in full, as text: "Alicia T. - Kitchen customer · Measuring on Thursday". The same
     * words `Card` shows (`SubWords`), so nothing here can say something the row does not.
     */
    public static WholeRow = (One: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return nz(One.Title).trim() + " - " + AppViews.SubWords(One);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.WholeRow");
            return "";
        }
    };

    /** The second line's words (unescaped): who they are and what is going on, or why there is nothing to say. */
    public static SubWords = (One: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (One.Harvested === false) { return "Not analysed yet"; }
            const Bits: string[] = [];
            if (One.Relationship.length > 0) { Bits.push(One.Relationship); }
            if (One.Summary.length > 0) { Bits.push(One.Summary); }
            if (Bits.length === 0) { return "Nothing to note"; }
            return Bits.join(" · ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.SubWords");
            return "";
        }
    };

    /** ⚠ NEVER AN EMPTY LINE. An unharvested contact has no relationship and no summary, and a
     *  blank row where every other card has two is read as a broken card, not as an unread one. */
    private static Sub = (One: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (One.Harvested === false) {
                return "<span class=\"r unread\">Not analysed yet</span>";
            }
            const Bits: string[] = [];
            if (One.Relationship.length > 0) { Bits.push(One.Relationship); }
            if (One.Summary.length > 0) { Bits.push(One.Summary); }
            if (Bits.length === 0) { return "<span class=\"r unread\">Nothing to note</span>"; }
            return "<span class=\"r\">" + kHTMLCore.EscapeAttr(Bits.join("  ·  ")) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Sub");
            return "";
        }
    };

    /**
     * The card's standing in plain words (design system, 2026-09-30: "plain wordings"): the index's word ("owed",
     * "active" ...) is a code, not a sentence - "owed" means the ball is in the owner's court.
     */
    public static StandingWords = (Standing: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ The words themselves are `AppChatStanding`'s - the contact page (Bridge) says the same ones.
            return AppChatStanding.Words(Standing);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.StandingWords");
            return "";
        }
    };

    /** The dot between a meta line's pieces - its own span, so a row that lays the pieces out in columns can drop it. */
    public static readonly Dot: string = "<span class=\"msep\">  ·  </span>";

    /**
     * The column names over the rows, for the eye only (`aria-hidden`: every row is a link that already says its own
     * words in order). Drawn only where the list is the main content at 800-1099px (`app.css`, block `wac-structure`) -
     * in the narrow left pane and on a phone a row is a single compact line and needs no header.
     */
    public static readonly ListHead: string = "<div class=\"listhead\" aria-hidden=\"true\"><span>Name</span><span>Relationship</span>"
        + "<span>Status</span><span>Last message</span><span>Open tasks</span></div>";

    /** ⭐ The phone's marks beside the name (`AppChatMarks`): pinned, locked - icons with words for a screen reader. */
    private static PhoneMarks = (One: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = "";
            if (One.PinnedAt > 0) { Out = Out + "<span class=\"rowmark pinmark\" title=\"Pinned on your phone\">" + AppIcons.Svg(AppIcons.Pin, 14) + "<span class=\"sr\"> (pinned)</span></span>"; }
            if (One.Locked === true) { Out = Out + "<span class=\"rowmark lockmark\" title=\"Locked on your phone\">" + AppIcons.Svg(AppIcons.Lock, 14) + "<span class=\"sr\"> (locked)</span></span>"; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.PhoneMarks");
            return "";
        }
    };

    private static Badge = (One: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ "N new" FIRST (`AppLiveList`): their messages since the page loaded - what WhatsApp's green count says -
            // merged with the PHONE's own unread count (`AppChatMarksRules.BadgeCount`: the larger; "Unread" when marked).
            let Fresh: string = "";
            const Count: number = AppChatMarksRules.BadgeCount(One);
            if (Count > 0) { Fresh = AppViews.Dot + "<i class=\"badge fresh\">" + Count.toLocaleString() + " new</i>"; }
            if (Count === -1) { Fresh = AppViews.Dot + "<i class=\"badge fresh\">Unread</i>"; }
            if (One.OpenTasks <= 0) { return Fresh; }
            // ⭐ OWNER 2026-11-01: a task icon + the number instead of "N to do"; the words stay for a screen reader + title.
            let Words: string = String(One.OpenTasks) + " open tasks";
            if (One.OpenTasks === 1) { Words = "1 open task"; }
            return Fresh + AppViews.Dot + "<i class=\"badge tasksbadge\" title=\"" + kHTMLCore.EscapeAttr(Words) + "\">"
                + AppIcons.Svg(AppIcons.NavTasks, 12) + "<span aria-hidden=\"true\">" + String(One.OpenTasks)
                + "</span><span class=\"sr\">" + kHTMLCore.EscapeHtml(Words) + "</span></i>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Badge");
            return "";
        }
    };

    // ── Tasks ────────────────────────────────────────────────────────────────────────────────

    public static Tasks = (Index: AppIndex): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Index.Tasks.length === 0) { return AppViews.Empty("open tasks", Index); }
            const Out: string[] = ["<ul class=\"rows\">"];
            for (const One of Index.Tasks) { Out.push(AppViews.TaskRow(One)); }
            Out.push("</ul>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Tasks");
            return "";
        }
    };

    private static TaskRow = (One: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return `<li class="row">${AppViews.Open(One)}<span class="l">${kHTMLCore.EscapeAttr(One.Label)}</span>`
                + `<span class="v">${kHTMLCore.EscapeAttr(One.Value)}</span><span class="w">${kHTMLCore.EscapeAttr(One.Contact)}</span></a></li>`;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.TaskRow");
            return "";
        }
    };

    /**
     * ⚠ THE ROW LINKS TO THE MESSAGE, NOT JUST THE CONTACT. A task in a cross-contact list with no
     * route back to the sentence that created it is the claim-without-evidence the block engine
     * exists to stop - and an obligation you cannot check is one you will not act on.
     */
    private static Open = (One: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const At: string = AppTaskRules.LinkOf(One);
            if (At.length === 0) { return ""; }
            return "<a href=\"" + kHTMLCore.EscapeAttr(At) + "\">";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Open");
            return "";
        }
    };

    // ── Revenue ──────────────────────────────────────────────────────────────────────────────

    /**
     * ⚠ TOTALS ARE PER CURRENCY AND PER DIRECTION, NEVER ONE NUMBER. Adding EUR to SEK produces a
     * figure that is wrong in both; adding money owed TO the owner to money owed BY them produces
     * one that is wrong in every currency. The harvest is told never to guess `direction`, and
     * anything it left as `none` is listed but NOT totalled - visible, and excluded from a sum that
     * would otherwise be quietly fictional.
     */
    public static Money = (Index: AppIndex): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Index.Money.length === 0) { return AppViews.Empty("money", Index); }
            const Out: string[] = [AppViews.Totals(Index.Money), "<ul class=\"rows\">"];
            for (const One of Index.Money) { Out.push(AppViews.MoneyRow(One)); }
            Out.push("</ul>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Money");
            return "";
        }
    };

    private static Totals = (Rows: AppRow[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Sums: Record<string, number> = {};
            for (const One of Rows) {
                if (One.Money === null) { continue; }
                if (One.Money.Direction === "none") { continue; }
                if (One.Money.Settled === true) { continue; }
                const Key: string = One.Money.Direction + " " + One.Money.Currency;
                let Now: number = 0;
                if (typeof Sums[Key] === "number") { Now = Sums[Key]; }
                Sums[Key] = Now + One.Money.Amount;
            }
            const Keys: string[] = Object.keys(Sums);
            if (Keys.length === 0) { return ""; }
            const Out: string[] = ["<p class=\"sums\">"];
            for (const Key of Keys) {
                const Way: string = Key.split(" ")[0];
                let Word: string = "owed to you";
                if (Way === "out") { Word = "you owe"; }
                Out.push("<b>" + kHTMLCore.EscapeAttr(AppViews.Amount(Sums[Key],
                    Key.split(" ")[1])) + "</b> " + Word + "  ");
            }
            Out.push("<i>unsettled only</i></p>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Totals");
            return "";
        }
    };

    private static MoneyRow = (One: AppRow): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Sum: string = "";
            if (One.Money !== null) {
                Sum = AppViews.Amount(One.Money.Amount, One.Money.Currency);
            }
            return "<li class=\"row money\">" + AppViews.Open(One)
                + "<span class=\"l\">" + kHTMLCore.EscapeAttr(One.Label) + "</span>"
                + "<span class=\"v\"><b>" + kHTMLCore.EscapeAttr(Sum) + "</b> "
                + kHTMLCore.EscapeAttr(AppViews.Way(One.Money)) + "</span>"
                + "<span class=\"w\">" + kHTMLCore.EscapeAttr(One.Contact) + "</span>"
                + "</a></li>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.MoneyRow");
            return "";
        }
    };

    private static Way = (Money: AppMoney | null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Money === null) { return ""; }
            if (Money.Settled === true) { return "settled"; }
            if (Money.Direction === "in") { return "owed to you"; }
            if (Money.Direction === "out") { return "you owe"; }
            return "not totalled";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Way");
            return "";
        }
    };

    /**
     * ⚠ NOT EVERY CURRENCY HAS CENTS. ISO 4217 gives each one an exponent, and for JPY, KRW, VND
     * and ISK it is ZERO - the minor unit IS the major unit. Dividing those by 100 turns 1,488,000
     * VND into "14,880.00 VND": a plausible-looking number, wrong by a factor of a hundred, on the
     * one screen where being quietly wrong matters most. Found because a harvest correctly wrote
     * VND at exponent 0 while this divided unconditionally.
     *
     * The list is the zero-exponent currencies this archive can realistically contain. Anything not
     * named here is treated as two decimals, which is right for the overwhelming majority.
     */
    public static readonly Whole: string[] = ["JPY", "KRW", "VND", "ISK", "CLP", "XAF", "XOF"];

    /** Minor units in, text out. The only place in the pipeline where a decimal point exists. */
    public static Amount = (Minor: number, Currency: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppViews.Whole.indexOf(Currency.toUpperCase()) >= 0) {
                return Minor.toLocaleString() + " " + Currency;
            }
            const Big: number = Math.floor(Minor / 100);
            let Cents: string = String(Minor % 100);
            if (Cents.length < 2) { Cents = "0" + Cents; }
            return Big.toLocaleString() + "." + Cents + " " + Currency;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppViews.Amount");
            return "";
        }
    };

    public static Ago = (Days: number): string => {
        return AppElapsedDays.Words(Days, "AppViews.Ago");
    };
}
